import {
	action,
	autorun,
	computed,
	observable,
	makeObservable,
	reaction,
	runInAction,
} from 'mobx';
import {attributesStore} from '@netx/core-attribute/store/attributesStore';
import {AttributeTemplateType} from '@netx/core-attribute/schema/attributeTemplate';
import {vocabularyValueLookup} from '@netx/core-attribute-vocabulary/api/vocabularyValueLookup';
import {getPersonById} from '@netx/core-person/api/getPersonById';
import {RuleModel} from '../baseModel';
import {
	MetadataType,
	MetadataNoValueTypes,
	SearchTypeMetadata,
	TSearchTypeMetadata,
} from './constants';
import {metadataRuleDefaults} from './defaults';
import type {MetadataRule} from './types';
import {
	getMetadataTypeOptions,
	metadataNameHackIn,
	metadataNameHackOut,
} from './utils';
import {validateMetadataRule} from './validate';

export class MetadataRuleModel
	extends RuleModel<TSearchTypeMetadata>
	implements Omit<MetadataRule, 'elementValue1' | 'elementValue3'>
{
	readonly elementType = SearchTypeMetadata;
	elementSubType1: MetadataType = metadataRuleDefaults.elementSubType1;
	elementSubType2: 0 = metadataRuleDefaults.elementSubType2;
	elementValue1: string | string[] = metadataRuleDefaults.elementValue1;
	elementValue2: string = metadataRuleDefaults.elementValue2;
	elementValue3: string | '' = metadataRuleDefaults.elementValue3;

	checkedElementValue1 = false;

	get elementSubType1Options() {
		return getMetadataTypeOptions(this.elementValue2);
	}

	get isValid() {
		const validation = validateMetadataRule(this as MetadataRule);
		this.setValidationErrors(this.dirty && validation ? validation : undefined);
		return !validation;
	}

	constructor(params?: Partial<Omit<MetadataRule, 'elementType'>>) {
		super();

		makeObservable(this, {
			elementSubType1: observable,
			elementSubType1Options: computed,
			elementValue1: observable,
			elementValue2: observable,
			elementValue3: observable,
			isValid: computed,
			setElementSubType1: action,
			setElementValue1: action,
			setElementValue2: action,
			setElementValue3: action,
		});

		runInAction(() => {
			if (params?.elementValue2) {
				params.elementValue2 = metadataNameHackIn(params.elementValue2);
			}
			Object.assign(this, {...metadataRuleDefaults, ...params});
		});

		this.reactionDisposers.push(
			reaction(
				() => this.elementSubType1Options,
				(elementSubType1Options) => {
					// select the first available option if selected is no longer available
					if (!elementSubType1Options.includes(this.elementSubType1)) {
						this.setElementSubType1(elementSubType1Options[0]);
					}
				},
			),
			reaction(
				() => this.elementSubType1,
				(elementSubType1) => {
					if (![MetadataType.MATCH_ANY_RANGE].includes(elementSubType1)) {
						// no range end value required
						this.elementValue3 = '';
					}

					if (
						[
							MetadataType.MATCH_VALUE_IS_EMPTY,
							MetadataType.MATCH_VALUE_IS_NOT_EMPTY,
						].includes(elementSubType1 as any)
					) {
						// no value is required
						this.elementValue1 = '';
						this.dirty = false;
					}
				},
			),
			reaction(
				() => this.elementValue2,
				(elementValue2) => {
					this.dirty = false;
					// reset values when selected attribute changes
					this.elementValue1 = '';
					this.elementValue3 = '';
				},
			),
			autorun(async () => {
				if (this.elementValue2 === '') {
					this.setElementValue2Unavailable(false);
					return;
				}

				const attr = attributesStore.getAttributeByName(this.elementValue2);
				if (this.elementValue2 && this.elementValue1 && attr) {
					if (!this.checkedElementValue1) {
						this.checkedElementValue1 = true;
						// validate selected values as they may have been deleted since creation
						if (
							[
								AttributeTemplateType.TAG,
								AttributeTemplateType.SELECT,
							].includes(attr.type)
						) {
							this.setElementValue1Loading(true);
							try {
								if (attr.spec1) {
									const results = await vocabularyValueLookup({
										id: attr.spec1,
										query: '""',
									});

									if (
										typeof this.elementValue1 === 'string' &&
										this.elementValue1
									) {
										const unavailable = !results.includes(this.elementValue1);
										this.setElementValue1Unavailable(unavailable);
										// If the value is unavailable, reset it
										if (unavailable) this.setElementValue1('');
									} else if (
										Array.isArray(this.elementValue1) &&
										this.elementValue1.length > 0
									) {
										const validItems = this.elementValue1.filter((value) =>
											results.includes(value),
										);
										const allItemsAreValid =
											validItems.length === this.elementValue1.length;
										this.setElementValue1Unavailable(!allItemsAreValid);
										// only keep the valid items
										if (!allItemsAreValid) this.setElementValue1(validItems);
									}
								}
								// assume people
								// eventually this may have a spec1 when we allow for more management
								// in phase 2 of this work
								else {
									if (
										typeof this.elementValue1[0] === 'string' &&
										this.elementValue1[0].length > 0
									) {
										const result = await getPersonById({
											id: this.elementValue1[0] as string,
										});
										if (!result) {
											this.setElementValue1Unavailable(true);
										} else {
											this.setElementValue1Label(result.name);
										}
									}
								}
							} finally {
								this.setElementValue1Loading(false);
							}
						}
					}
				}
				this.setElementValue2Unavailable(attr == null);
			}),
		);
	}

	/**
	 * Match criteria
	 * @param elementSubType1 MetadataType
	 */
	setElementSubType1(elementSubType1: MetadataType) {
		this.elementSubType1 = elementSubType1;
	}

	/** stubbed */
	setElementSubType2() {}

	/**
	 * Value or range start value
	 * @param elementValue1 value | range start
	 */
	setElementValue1(elementValue1: string | string[]) {
		this.elementValue1 = elementValue1;
		this.dirty = true;
	}

	/**
	 * Update selected attribute
	 * @param elementValue2 attribute name
	 */
	setElementValue2(elementValue2: string) {
		// TODO: should we enforce? See the validation as well
		// const attribute = attributesStore.getAttributeByName(elementValue2);
		// if (!attribute) return;

		this.elementValue2 = elementValue2;
	}

	/**
	 * Range end value\
	 * This is a little tricky to enforce on - if the attribute type selected is a number type
	 * then we can support range values
	 * @param elementValue3 Range end (if applicable)
	 */
	setElementValue3(elementValue3: string | '') {
		this.elementValue3 = elementValue3;
		this.dirty = true;
	}

	/** @override */
	toRuleObject(): MetadataRule<any> | MetadataRule<any>[] {
		const ruleObjectBase = {
			elementType: this.elementType,
			elementSubType1: this.elementSubType1,
			elementSubType2: this.elementSubType2,
			elementValue2: metadataNameHackOut(this.elementValue2),
			elementValue3: this.elementValue3,
		};

		if (MetadataNoValueTypes.includes(ruleObjectBase.elementSubType1 as any)) {
			return {
				...ruleObjectBase,
				elementValue1: '',
				elementValue3: '',
			};
		}

		// multiple values
		if (Array.isArray(this.elementValue1)) {
			return this.elementValue1.map((elementValue1) => ({
				...ruleObjectBase,
				elementValue1,
			}));
		}

		// just a regular single value rule
		return {
			...ruleObjectBase,
			elementValue1: this.elementValue1,
		};
	}
}

export default MetadataRuleModel;
